'use client';

import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/cn';

export const CONTROL_CLASS =
  'corner-brand-sm h-8 w-full border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 transition-colors outline-none focus:border-accent-bright focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 pointer-coarse:h-11 pointer-coarse:text-base';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(CONTROL_CLASS, className)} {...rest} />;
  }
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(CONTROL_CLASS, 'h-auto min-h-[4.5rem] resize-y py-1.5 leading-5 pointer-coarse:h-auto', className)}
        {...rest}
      />
    );
  }
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <span className={cn('relative inline-flex w-full', className)}>
        <select ref={ref} className={cn(CONTROL_CLASS, 'appearance-none pr-7')} {...rest}>
          {children}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2 text-zinc-500"
        />
      </span>
    );
  }
);

export const SearchInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function SearchInput({ className, ...rest }, ref) {
    return (
      <span className={cn('relative inline-flex w-full', className)}>
        <Search
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-zinc-500"
        />
        <input ref={ref} type="search" className={cn(CONTROL_CLASS, 'pl-8')} {...rest} />
      </span>
    );
  }
);

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, ...rest },
  ref
) {
  return (
    <label
      className={cn(
        // `relative`: the real input is `sr-only`, i.e. absolutely positioned.
        // Without a positioned label it resolves against the page instead, and
        // inside a horizontally scrolling table it escapes the clip and widens
        // the document by however far its column sits off-screen.
        'relative inline-flex cursor-pointer items-center gap-2 text-sm text-zinc-200 pointer-coarse:min-h-11',
        rest.disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <input ref={ref} type="checkbox" className="peer sr-only" {...rest} />
      <span
        aria-hidden
        className="corner-brand-xs flex size-4 shrink-0 items-center justify-center border border-zinc-600 bg-zinc-900 transition-colors peer-checked:border-accent peer-checked:bg-accent peer-checked:[&>svg]:opacity-100 peer-focus-visible:outline-2 peer-focus-visible:outline-accent-bright"
      >
        <Check className="size-3 text-white opacity-0" strokeWidth={3} />
      </span>
      {label}
    </label>
  );
});

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio({ label, className, ...rest }, ref) {
  return (
    <label
      className={cn(
        // `relative` for the same reason as Checkbox: the sr-only input must
        // stay clipped inside its own label.
        'relative inline-flex cursor-pointer items-start gap-2 text-sm text-zinc-200 pointer-coarse:min-h-11',
        rest.disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <input ref={ref} type="radio" className="peer sr-only" {...rest} />
      <span
        aria-hidden
        className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-zinc-600 bg-zinc-900 transition-colors peer-checked:border-accent peer-checked:[&>span]:opacity-100 peer-focus-visible:outline-2 peer-focus-visible:outline-accent-bright"
      >
        <span className="size-2 rounded-full bg-accent opacity-0" />
      </span>
      {label}
    </label>
  );
});
